import type {
  ApiResponse,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  CreateSearchRequest,
  GetConfigData,
  MeData,
  OrganizationSummary,
  SaveConfigRequest,
  UpdateSearchRequest,
} from '@legalhermes/shared/api-contracts';
import type { Change, MonitorStats, Search, Snapshot } from '@legalhermes/shared/domain';
import { supabase } from '../lib/supabase';

const BASE = `${import.meta.env.VITE_API_URL || ''}/api`;

/** Código que el backend envía cuando el usuario no pertenece a ninguna organización. */
export const ORG_REQUIRED_CODE = 'ORG_REQUIRED';

/** El usuario está autenticado pero aún no tiene organización → onboarding. */
export class OrgRequiredError extends Error {
  code = ORG_REQUIRED_CODE;
  constructor() {
    super('El usuario no pertenece a ninguna organización');
    this.name = 'OrgRequiredError';
  }
}

export function isOrgRequiredError(error: unknown): error is OrgRequiredError {
  return error instanceof OrgRequiredError;
}

/** El backend no respondió (caído, sin red). Se distingue de un error de app. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('No se pudo conectar con el servidor');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/** Igual a RequestInit, pero admite un body de objeto plano que fetchJSON serializa a JSON. */
interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | object | null;
}

async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function fetchJSON<T>(url: string, options: FetchOptions = {}): Promise<T> {
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers,
      body: options.body
        ? typeof options.body === 'string'
          ? options.body
          : JSON.stringify(options.body)
        : undefined,
    } as RequestInit);
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (res.status === 401) {
    await supabase.auth.signOut();
    throw new Error('Session expired');
  }

  // El usuario está autenticado pero no pertenece a ninguna organización.
  // Se expone como error tipado para que el guard de routing redirija al
  // onboarding, en vez de navegar desde aquí (efecto oculto en la capa de red).
  if (res.status === 403) {
    const body = (await res
      .clone()
      .json()
      .catch(() => null)) as { code?: string } | null;
    if (body?.code === ORG_REQUIRED_CODE) throw new OrgRequiredError();
  }

  return res.json() as Promise<T>;
}

export const api = {
  // Config
  getConfig: async (): Promise<Partial<GetConfigData>> => {
    const res = await fetchJSON<ApiResponse<GetConfigData>>(`${BASE}/config`);
    return res.data || {};
  },

  saveConfig: (config: SaveConfigRequest) =>
    fetchJSON<ApiResponse<unknown>>(`${BASE}/config`, { method: 'POST', body: config }),

  // Stats
  getStatsByName: async (): Promise<MonitorStats | null> => {
    const res = await fetchJSON<ApiResponse<MonitorStats>>(`${BASE}/stats/by-name`);
    return res.data || null;
  },

  getStatsByProcess: async (): Promise<MonitorStats | null> => {
    const res = await fetchJSON<ApiResponse<MonitorStats>>(`${BASE}/stats/by-process`);
    return res.data || null;
  },

  // Historical data
  getHistoricalDataByName: async (): Promise<Record<string, Snapshot>> => {
    const res = await fetchJSON<ApiResponse<Record<string, Snapshot>>>(`${BASE}/data/by-name`);
    return res.data || {};
  },

  getHistoricalDataByProcess: async (): Promise<Record<string, Snapshot>> => {
    const res = await fetchJSON<ApiResponse<Record<string, Snapshot>>>(`${BASE}/data/by-process`);
    return res.data || {};
  },

  getRecentChanges: async (limit = 50): Promise<Change[]> => {
    const res = await fetchJSON<ApiResponse<Change[]>>(
      `${BASE}/data/recent-changes?limit=${limit}`
    );
    return res.data || [];
  },

  // Delete
  deleteByName: (name: string) =>
    fetchJSON<ApiResponse<unknown>>(`${BASE}/data/by-name/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  deleteByProcess: (number: string) =>
    fetchJSON<ApiResponse<unknown>>(`${BASE}/data/by-process/${encodeURIComponent(number)}`, {
      method: 'DELETE',
    }),

  deleteProcessById: (idProceso: number | string) =>
    fetchJSON<ApiResponse<unknown>>(`${BASE}/data/process/${encodeURIComponent(idProceso)}`, {
      method: 'DELETE',
    }),

  // Searches
  getSearches: async (): Promise<Search[]> => {
    const res = await fetchJSON<ApiResponse<Search[]>>(`${BASE}/searches`);
    return res.data || [];
  },

  addSearch: async (searchData: CreateSearchRequest): Promise<Search> => {
    const res = await fetchJSON<ApiResponse<Search>>(`${BASE}/searches`, {
      method: 'POST',
      body: searchData,
    });
    if (res && res.success === false) throw new Error(res.error || 'Error al agregar búsqueda');
    return (res as { data: Search }).data;
  },

  updateSearch: async (id: string, updates: UpdateSearchRequest): Promise<Search> => {
    const res = await fetchJSON<ApiResponse<Search>>(`${BASE}/searches/${id}`, {
      method: 'PUT',
      body: updates,
    });
    if (res && res.success === false) throw new Error(res.error || 'Error al actualizar búsqueda');
    return (res as { data: Search }).data;
  },

  deleteSearch: (id: string) =>
    fetchJSON<ApiResponse<unknown>>(`${BASE}/searches/${id}`, { method: 'DELETE' }),

  // Me
  getMe: async (): Promise<MeData | null> => {
    const res = await fetchJSON<ApiResponse<MeData>>(`${BASE}/me`);
    return res.data || null;
  },

  // Organizations
  createOrganization: async (body: CreateOrganizationRequest): Promise<OrganizationSummary> => {
    const res = await fetchJSON<CreateOrganizationResponse>(`${BASE}/organizations`, {
      method: 'POST',
      body,
    });
    if (!res.success) {
      throw new Error(res.error || 'No se pudo crear la organización');
    }
    return res.data;
  },
};
